import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { regionConfig, currentRegion } from "../config/regions";

/** Modal eyebrow: region-correct brand, localized where defined. */
function contactBrandEyebrow(lang) {
  const t = translations[lang] ?? translations.en;
  const region = currentRegion === "us" ? "us" : "cis";
  return t.eyebrow?.[region] ?? regionConfig.brand_name;
}

// ContactWidget — Floating feedback button + modal
// Props:
//   lang        – "en" | "ru"  (from your existing language toggle)
//   apiEndpoint – string, default "/api/contact"

const AUTH_ROUTES = [
  "/login",
  "/signup",
  "/auth/callback",
  "/reset-password",
  "/privacy",
  "/terms",
  "/cookies",
];

function isClinicalRoute(pathname) {
  return !AUTH_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

export default function ContactWidget({ lang = "en", apiEndpoint = "/api/contact" }) {
  const { pathname } = useLocation();
  const [clinicalWorkspace, setClinicalWorkspace] = useState(false);

  useEffect(() => {
    const onClinicalRoute = isClinicalRoute(pathname);
    const hasClinicalShell = !!document.querySelector(".theme-clinical");
    setClinicalWorkspace(onClinicalRoute && hasClinicalShell);
  }, [pathname]);

  const themeClass = clinicalWorkspace ? "mc-theme-clinical" : "mc-theme-brand";
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  // `website` is a honeypot (WP2): visually hidden, never filled by humans; the
  // backend silently drops submissions where it is non-empty.
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const firstInputRef = useRef(null);
  const overlayRef = useRef(null);

  const t = translations[lang] ?? translations.en;

  // Focus trap + ESC close
  useEffect(() => {
    if (open) {
      setTimeout(() => firstInputRef.current?.focus(), 80);
      const onKey = (e) => { if (e.key === "Escape") handleClose(); };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }
  }, [open]);

  const handleClose = () => {
    if (status === "sending") return;
    setOpen(false);
    setStatus("idle");
    setErrorMsg("");
    setForm({ name: "", email: "", message: "", website: "" });
  };

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) handleClose();
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus("error");
      setErrorMsg(t.errorRequired);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setStatus("error");
      setErrorMsg(t.errorEmail);
      return;
    }

    setStatus("sending");
    setErrorMsg("");

    try {
      const res = await fetch(apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, lang }),
      });
      if (!res.ok) throw new Error(await res.text());
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(t.errorServer);
    }
  };

  if (pathname === "/share" || pathname.startsWith("/share/")) {
    return null;
  }

  return (
    <div className={themeClass}>
      <style>{styles}</style>

      {/* Floating button */}
      {collapsed ? (
        <button
          className="mc-fab mc-fab--collapsed"
          onClick={() => setCollapsed(false)}
          aria-label={t.fabLabel}
          data-testid="contact-widget-fab-collapsed"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
      ) : (
        <div className="mc-fab-group" data-testid="contact-widget-fab">
          <button
            className="mc-fab"
            onClick={() => setOpen(true)}
            aria-label={t.fabLabel}
            title={t.fabLabel}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <span className="mc-fab-label">{t.fabLabel}</span>
          </button>
          <button
            className="mc-fab-hide"
            onClick={() => setCollapsed(true)}
            aria-label="Hide"
            data-testid="contact-widget-hide"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 6 15 12 9 18"/>
            </svg>
          </button>
        </div>
      )}

      {/* Overlay + Modal */}
      {open && (
        <div className="mc-overlay" ref={overlayRef} onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-label={t.title}>
          <div className="mc-modal" data-testid="contact-widget-modal">

            {/* Header */}
            <div className="mc-modal-header">
              <div className="mc-modal-title-block">
                <span className="mc-modal-eyebrow">{contactBrandEyebrow(lang)}</span>
                <h2 className="mc-modal-title">{t.title}</h2>
              </div>
              <button className="mc-close-btn" onClick={handleClose} aria-label="Close" data-testid="contact-widget-close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Success state */}
            {status === "success" ? (
              <div className="mc-success" data-testid="contact-widget-success">
                <div className="mc-success-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <p className="mc-success-title">{t.successTitle}</p>
                <p className="mc-success-sub">{t.successSub}</p>
                <button className="mc-btn-primary" onClick={handleClose}>{t.close}</button>
              </div>
            ) : (
              /* Form */
              <div className="mc-form">
                {/* Honeypot — off-screen, excluded from tab order and assistive tech. No i18n: never shown. */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}>
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                    data-testid="contact-honeypot-input"
                  />
                </div>
                <div className="mc-field">
                  <label className="mc-label">{t.name}</label>
                  <input
                    ref={firstInputRef}
                    className="mc-input"
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder={t.namePlaceholder}
                    disabled={status === "sending"}
                    autoComplete="name"
                    data-testid="contact-name-input"
                  />
                </div>

                <div className="mc-field">
                  <label className="mc-label">{t.email}</label>
                  <input
                    className="mc-input"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder={t.emailPlaceholder}
                    disabled={status === "sending"}
                    autoComplete="email"
                    data-testid="contact-email-input"
                  />
                </div>

                <div className="mc-field">
                  <label className="mc-label">{t.message}</label>
                  <textarea
                    className="mc-textarea"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder={t.messagePlaceholder}
                    rows={4}
                    disabled={status === "sending"}
                    data-testid="contact-message-input"
                  />
                </div>

                {status === "error" && (
                  <p className="mc-error" data-testid="contact-error">{errorMsg}</p>
                )}

                <button
                  className="mc-btn-primary"
                  onClick={handleSubmit}
                  disabled={status === "sending"}
                  data-testid="contact-submit-btn"
                >
                  {status === "sending" ? (
                    <span className="mc-spinner" />
                  ) : t.send}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Translations
const translations = {
  en: {
    fabLabel: "Contact us",
    eyebrow: { cis: "MedConsilium", us: "MedConcil" },
    title: "Get in touch",
    name: "Name",
    namePlaceholder: "Dr. John Smith",
    email: "Email",
    emailPlaceholder: "you@clinic.com",
    message: "Message",
    messagePlaceholder: "Your question, request or feedback...",
    send: "Send message",
    close: "Close",
    successTitle: "Message sent",
    successSub: "We'll get back to you within one business day.",
    errorRequired: "Please fill in all fields.",
    errorEmail: "Enter a valid email address.",
    errorServer: "Something went wrong. Please try again.",
  },
  ru: {
    fabLabel: "Написать нам",
    eyebrow: { cis: "МедКонсилиум", us: "MedConcil" },
    title: "Обратная связь",
    name: "Имя",
    namePlaceholder: "Иван Петров",
    email: "Email",
    emailPlaceholder: "you@clinic.ru",
    message: "Сообщение",
    messagePlaceholder: "Ваш вопрос, запрос или пожелание...",
    send: "Отправить",
    close: "Закрыть",
    successTitle: "Сообщение отправлено",
    successSub: "Мы ответим в течение одного рабочего дня.",
    errorRequired: "Пожалуйста, заполните все поля.",
    errorEmail: "Введите корректный адрес email.",
    errorServer: "Ошибка сервера. Попробуйте ещё раз.",
  },
};

// Styles
const styles = `
  /* Brand tokens (auth shell — matches tailwind brand palette) */
  :root {
    --mc-navy: #0B1929;
    --mc-navy-light: #132038;
    --mc-amber: #2458D3;
    --mc-amber-dark: #1B46A8;
    --mc-white: #ffffff;
    --mc-text-muted: rgba(255,255,255,0.55);
    --mc-border: rgba(255,255,255,0.12);
    --mc-radius: 14px;
    --mc-radius-sm: 8px;
    --mc-font-display: 'Lora', Georgia, serif;
    --mc-font-body: 'Inter', system-ui, sans-serif;
    --mc-shadow: 0 24px 64px rgba(0,0,0,0.45), 0 4px 16px rgba(0,0,0,0.25);
    --mc-clinical-bg: #F8FAFC;
    --mc-clinical-text: #0F172A;
    --mc-clinical-muted: #64748B;
    --mc-clinical-border: #E2E8F0;
    --mc-clinical-primary: #2458D3;
  }

  /* FAB group */
  .mc-fab-group {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 9000;
    display: flex;
    align-items: center;
    gap: 0;
    animation: mc-fab-slide-in 0.25s ease;
  }
  @keyframes mc-fab-slide-in {
    from { opacity: 0; transform: translateX(20px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  /* FAB */
  .mc-fab {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px 10px 12px;
    background: var(--mc-amber);
    color: #ffffff;
    border: none;
    border-radius: 50px 0 0 50px;
    cursor: pointer;
    font-family: var(--mc-font-body);
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.01em;
    box-shadow: 0 4px 16px rgba(36,88,211,0.35), 0 1px 4px rgba(0,0,0,0.15);
    transition: background 0.15s ease;
  }
  .mc-fab:hover {
    background: var(--mc-amber-dark);
  }

  /* Brand shell (auth routes) — explicit class matches default FAB styling */
  .mc-theme-brand .mc-fab,
  .mc-theme-brand .mc-fab-hide,
  .mc-theme-brand .mc-fab--collapsed {
    background: var(--mc-amber);
    color: #ffffff;
  }

  /* Clinical workspace variant */
  .mc-theme-clinical .mc-fab,
  .mc-theme-clinical .mc-fab-hide,
  .mc-theme-clinical .mc-fab--collapsed {
    background: var(--mc-clinical-primary);
    color: var(--mc-white);
    box-shadow: 0 4px 16px rgba(36,88,211,0.25), 0 1px 4px rgba(0,0,0,0.1);
  }
  .mc-theme-clinical .mc-fab:hover,
  .mc-theme-clinical .mc-fab-hide:hover,
  .mc-theme-clinical .mc-fab--collapsed:hover {
    background: #1d4ed8;
  }
  .mc-theme-clinical .mc-fab-hide {
    border-left-color: rgba(255,255,255,0.2);
  }
  .mc-theme-clinical .mc-overlay {
    background: rgba(15,23,42,0.4);
  }
  .mc-theme-clinical .mc-modal {
    background: var(--mc-clinical-bg);
    border-color: var(--mc-clinical-border);
    box-shadow: 0 24px 64px rgba(15,23,42,0.12), 0 4px 16px rgba(15,23,42,0.08);
  }
  .mc-theme-clinical .mc-modal-eyebrow {
    color: var(--mc-clinical-primary);
  }
  .mc-theme-clinical .mc-modal-title,
  .mc-theme-clinical .mc-success-title {
    font-family: var(--mc-font-body);
    color: var(--mc-clinical-text);
  }
  .mc-theme-clinical .mc-close-btn { color: var(--mc-clinical-muted); }
  .mc-theme-clinical .mc-close-btn:hover { color: var(--mc-clinical-text); background: rgba(15,23,42,0.06); }
  .mc-theme-clinical .mc-label { color: var(--mc-clinical-muted); text-transform: none; letter-spacing: normal; }
  .mc-theme-clinical .mc-input,
  .mc-theme-clinical .mc-textarea {
    background: #ffffff;
    border-color: var(--mc-clinical-border);
    color: var(--mc-clinical-text);
  }
  .mc-theme-clinical .mc-input::placeholder,
  .mc-theme-clinical .mc-textarea::placeholder { color: #94a3b8; }
  .mc-theme-clinical .mc-input:focus,
  .mc-theme-clinical .mc-textarea:focus {
    border-color: var(--mc-clinical-primary);
    background: #ffffff;
  }
  .mc-theme-clinical .mc-btn-primary {
    background: var(--mc-clinical-primary);
    color: var(--mc-white);
  }
  .mc-theme-clinical .mc-btn-primary:hover:not(:disabled) {
    background: #1d4ed8;
  }
  .mc-theme-clinical .mc-success-icon {
    background: rgba(37,99,235,0.1);
    border-color: rgba(37,99,235,0.25);
    color: var(--mc-clinical-primary);
  }
  .mc-theme-clinical .mc-success-sub { color: var(--mc-clinical-muted); }

  /* Hide arrow button */
  .mc-fab-hide {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 38px;
    padding: 0;
    background: var(--mc-amber);
    color: #ffffff;
    border: none;
    border-left: 1px solid rgba(15,31,61,0.15);
    border-radius: 0 50px 50px 0;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.15s, background 0.15s;
    box-shadow: 0 4px 16px rgba(36,88,211,0.35), 0 1px 4px rgba(0,0,0,0.15);
  }
  .mc-fab-hide:hover {
    opacity: 1;
    background: var(--mc-amber-dark);
  }

  /* Collapsed state — small tab peeking from right edge */
  .mc-fab--collapsed {
    position: fixed;
    bottom: 28px;
    right: 0;
    z-index: 9000;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 40px;
    padding: 0;
    background: var(--mc-amber);
    color: #ffffff;
    border: none;
    border-radius: 8px 0 0 8px;
    cursor: pointer;
    opacity: 0.6;
    box-shadow: -2px 2px 8px rgba(0,0,0,0.2);
    transition: opacity 0.15s, width 0.15s;
    animation: mc-tab-in 0.2s ease;
  }
  .mc-fab--collapsed:hover {
    opacity: 1;
    width: 28px;
  }
  @keyframes mc-tab-in {
    from { opacity: 0; transform: translateX(10px); }
    to   { opacity: 0.6; transform: translateX(0); }
  }

  /* Overlay */
  .mc-overlay {
    position: fixed;
    inset: 0;
    z-index: 9100;
    background: rgba(5,10,22,0.72);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    padding: 28px;
    animation: mc-overlay-in 0.2s ease;
  }
  @keyframes mc-overlay-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  /* Modal */
  .mc-modal {
    width: 100%;
    max-width: 420px;
    background: var(--mc-navy);
    border: 1px solid var(--mc-border);
    border-radius: var(--mc-radius);
    box-shadow: var(--mc-shadow);
    overflow: hidden;
    animation: mc-modal-in 0.25s cubic-bezier(0.34,1.26,0.64,1);
  }
  @keyframes mc-modal-in {
    from { opacity: 0; transform: translateY(20px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* Header */
  .mc-modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 24px 24px 0;
  }
  .mc-modal-eyebrow {
    display: block;
    font-family: var(--mc-font-body);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--mc-amber);
    margin-bottom: 4px;
  }
  .mc-modal-title {
    margin: 0;
    font-family: var(--mc-font-display);
    font-size: 22px;
    font-weight: 700;
    color: var(--mc-white);
    line-height: 1.2;
  }
  .mc-close-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--mc-text-muted);
    padding: 4px;
    border-radius: 6px;
    transition: color 0.15s, background 0.15s;
    flex-shrink: 0;
  }
  .mc-close-btn:hover { color: var(--mc-white); background: rgba(255,255,255,0.08); }

  /* Form */
  .mc-form {
    padding: 20px 24px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .mc-field { display: flex; flex-direction: column; gap: 6px; }
  .mc-label {
    font-family: var(--mc-font-body);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--mc-text-muted);
  }
  .mc-input, .mc-textarea {
    background: rgba(255,255,255,0.06);
    border: 1px solid var(--mc-border);
    border-radius: var(--mc-radius-sm);
    color: var(--mc-white);
    font-family: var(--mc-font-body);
    font-size: 14px;
    padding: 11px 14px;
    outline: none;
    transition: border-color 0.15s, background 0.15s;
    width: 100%;
    box-sizing: border-box;
  }
  .mc-textarea { resize: vertical; min-height: 96px; }
  .mc-input::placeholder, .mc-textarea::placeholder { color: rgba(255,255,255,0.28); }
  .mc-input:focus, .mc-textarea:focus {
    border-color: var(--mc-amber);
    background: rgba(255,255,255,0.09);
  }
  .mc-input:disabled, .mc-textarea:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Error */
  .mc-error {
    margin: 0;
    font-family: var(--mc-font-body);
    font-size: 13px;
    color: #ff6b6b;
    padding: 10px 12px;
    background: rgba(255,107,107,0.1);
    border-radius: var(--mc-radius-sm);
    border: 1px solid rgba(255,107,107,0.2);
  }

  /* Button */
  .mc-btn-primary {
    width: 100%;
    padding: 13px;
    background: var(--mc-amber);
    color: #ffffff;
    border: none;
    border-radius: var(--mc-radius-sm);
    font-family: var(--mc-font-body);
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    letter-spacing: 0.02em;
    transition: background 0.15s, transform 0.15s;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 46px;
  }
  .mc-btn-primary:hover:not(:disabled) {
    background: var(--mc-amber-dark);
    transform: translateY(-1px);
  }
  .mc-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

  /* Spinner */
  .mc-spinner {
    width: 18px; height: 18px;
    border: 2px solid rgba(15,31,61,0.3);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: mc-spin 0.7s linear infinite;
  }
  @keyframes mc-spin { to { transform: rotate(360deg); } }

  /* Success */
  .mc-success {
    padding: 28px 24px 28px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 8px;
  }
  .mc-success-icon {
    width: 60px; height: 60px;
    border-radius: 50%;
    background: rgba(36,88,211,0.15);
    border: 1px solid rgba(36,88,211,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--mc-amber);
    margin-bottom: 8px;
  }
  .mc-success-title {
    margin: 0;
    font-family: var(--mc-font-display);
    font-size: 20px;
    font-weight: 700;
    color: var(--mc-white);
  }
  .mc-success-sub {
    margin: 0 0 16px;
    font-family: var(--mc-font-body);
    font-size: 14px;
    color: var(--mc-text-muted);
  }

  /* Mobile */
  @media (max-width: 500px) {
    .mc-overlay { padding: 0; align-items: flex-end; }
    .mc-modal { max-width: 100%; border-radius: var(--mc-radius) var(--mc-radius) 0 0; }
    .mc-fab-label { display: none; }
    .mc-fab { padding: 10px; border-radius: 50%; }
    .mc-fab-group { gap: 0; }
    .mc-fab-hide { display: none; }
  }
`;
